import { useEffect, useMemo, useRef, useState } from "react";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type {
  MotionConnectionRef,
  MotionEditableLayerKind,
  MotionLayerControlReport,
  MotionLayerCapabilities,
  MotionMediaReplacementCapability,
  MotionTargetQuery,
} from "@blings/blings-player/editor-tools";
import type { LayerInspectorSelection } from "../../../helpers/layerInspectorTools";
import { resolveSelectionLayerDetails } from "../../../helpers/layerInspectorPrefill";
import { ANCHOR_POSITIONS } from "./types";
import type { AnchorPosition, FieldLookup, VisualEditableField } from "./types";
import {
  recordStudioPerf,
  studioPerfNow,
} from "../../../helpers/studioPerfDebug";
import { areVectorsClose } from "./shapeStyleEdits";
import type { AccountFont } from "./utils";
import {
  buildCommonFieldLookup,
  buildFontSelectOptions,
  buildFieldLookup,
  CURATED_PROPERTIES,
  getActiveTextDocumentIndexForFrame,
  mergeConnectionControlsIntoFields,
  normalizeEditableFieldAnimationMetadata,
  resolveTextDocumentFieldsForFrame,
} from "./utils";

type UseLayerVisualPropertiesCapabilitiesConfig = {
  jsonFile: unknown;
  currentFrame: number;
  mainAssetKey: string | undefined;
  layerInspectorPrimarySelection: LayerInspectorSelection | undefined;
  layerInspectorSelections: LayerInspectorSelection[];
  mods: unknown[];
  data: unknown;
  liveControlData: unknown;
  accountFonts?: AccountFont[];
};

type ControlReportState = {
  reports: MotionLayerControlReport[];
  jsonFile: unknown;
  targetSignature: string;
  mods: unknown[];
  data: unknown;
  liveControlData: unknown;
};

type ResolvedLayerTarget = {
  target: MotionTargetQuery;
  key: string;
  fallbackTargets?: ResolvedLayerTarget[];
};

type CapabilityEntry = {
  target: MotionTargetQuery;
  key: string;
  capabilities: MotionLayerCapabilities;
  resolvedLayer?: unknown;
  controlReport?: MotionLayerControlReport;
};

const DISABLED_MEDIA_REPLACEMENT: MotionMediaReplacementCapability = {
  enabled: false,
  acceptedExtensions: [],
  reason: "Multiple layers selected",
};

const toConnectionRef = (
  connection: MotionLayerControlReport["connections"][number]
): MotionConnectionRef => ({
  id: connection.id,
  modIndex: connection.modIndex,
  modType: connection.modType,
  disabled: connection.disabled,
  target: connection.target,
  valueSource: connection.valueSource,
  writeBehavior: connection.writeBehavior,
});

const getTargetKey = (target: MotionTargetQuery) => {
  if (target.path) {
    return ["path", target.assetId ?? "main", target.path].join(":");
  }

  if (target.layerName) {
    return [
      "layer",
      target.assetId ?? "main",
      target.layerUid ?? "",
      target.layerName,
    ].join(":");
  }

  return ["path", target.assetId ?? "main", target.path ?? ""].join(":");
};

const createTarget = (
  selection: LayerInspectorSelection,
  jsonFile: unknown,
  mainAssetKey: string | undefined
): ResolvedLayerTarget | undefined => {
  const resolvedSelectionLayer = resolveSelectionLayerDetails(
    selection,
    jsonFile as any,
    mainAssetKey ?? "main"
  );

  if (!resolvedSelectionLayer?.layerName) {
    return undefined;
  }

  const directRootLayerPath =
    typeof selection.path === "string" && /^layers\[\d+\]$/.test(selection.path)
      ? selection.path
      : undefined;

  const identityTarget: MotionTargetQuery = {
    layerName: resolvedSelectionLayer.layerName,
    layerUid: resolvedSelectionLayer.layerUid,
    ...(resolvedSelectionLayer.assetId &&
    resolvedSelectionLayer.assetId !== mainAssetKey
      ? { assetId: resolvedSelectionLayer.assetId }
      : {}),
  };

  if (!directRootLayerPath) {
    return {
      target: identityTarget,
      key: getTargetKey(identityTarget),
    };
  }

  const pathTarget = {
    ...identityTarget,
    path: directRootLayerPath,
  };
  const identityKey = getTargetKey(identityTarget);
  const pathKey = getTargetKey(pathTarget);

  return {
    target: pathTarget,
    key: pathKey,
    fallbackTargets:
      pathKey === identityKey
        ? undefined
        : [{ target: identityTarget, key: identityKey }],
  };
};

const getCommonLayerKind = (
  capabilities: MotionLayerCapabilities[]
): MotionEditableLayerKind => {
  const firstKind = capabilities[0]?.layerKind;
  if (
    firstKind &&
    capabilities.every((capability) => capability.layerKind === firstKind)
  ) {
    return firstKind;
  }

  return "unknown";
};

const buildMultiCapabilities = (
  entries: CapabilityEntry[],
  editableFields: FieldLookup
): MotionLayerCapabilities | undefined => {
  const [primaryEntry] = entries;
  if (!primaryEntry) {
    return undefined;
  }

  const capabilities = entries.map((entry) => entry.capabilities);
  const fields = Array.from(editableFields.values());

  return {
    ...primaryEntry.capabilities,
    layerKind: getCommonLayerKind(capabilities),
    fields,
    animatedFields: fields.filter((field) => field.isAnimated),
    addableActions: [],
    supportedEffects: [],
    mediaReplacement: DISABLED_MEDIA_REPLACEMENT,
    canAlign: false,
    canEditTiming: capabilities.every((capability) => capability.canEditTiming),
    canReorder: false,
  };
};

const prepareFieldsForLayer = (
  fields: VisualEditableField[],
  layer: unknown,
  frame: number
) =>
  resolveTextDocumentFieldsForFrame(
    normalizeEditableFieldAnimationMetadata(fields, frame),
    layer,
    frame
  );

export function useLayerVisualPropertiesCapabilities({
  jsonFile,
  currentFrame,
  mainAssetKey,
  layerInspectorPrimarySelection,
  layerInspectorSelections,
  mods,
  data,
  liveControlData,
  accountFonts,
}: UseLayerVisualPropertiesCapabilitiesConfig) {
  const latestFrameRef = useRef(currentFrame);
  latestFrameRef.current = currentFrame;

  const activeSelections = useMemo(() => {
    if (layerInspectorSelections.length) {
      return layerInspectorSelections;
    }

    if (layerInspectorPrimarySelection) {
      return [layerInspectorPrimarySelection];
    }

    return [];
  }, [layerInspectorPrimarySelection, layerInspectorSelections]);
  const isMultiSelection = activeSelections.length > 1;

  const targetCandidates = useMemo<ResolvedLayerTarget[]>(() => {
    if (!jsonFile) {
      return [];
    }

    const seen = new Set<string>();
    return activeSelections.reduce<ResolvedLayerTarget[]>(
      (targets, selection) => {
        const candidate = createTarget(selection, jsonFile, mainAssetKey);
        if (!candidate) return targets;

        if (seen.has(candidate.key)) return targets;

        seen.add(candidate.key);
        targets.push(candidate);
        return targets;
      },
      []
    );
  }, [activeSelections, jsonFile, mainAssetKey]);

  const baseCapabilityEntries = useMemo<CapabilityEntry[]>(() => {
    const startedAt = studioPerfNow();
    const inspectionFrame = latestFrameRef.current;
    if (!jsonFile || !targetCandidates.length) {
      recordStudioPerf(
        "LayerVisualPropertiesCapabilities base",
        studioPerfNow() - startedAt,
        {
          entries: 0,
          frame: Math.round(inspectionFrame),
          targetKey: targetCandidates[0]?.key,
          targets: targetCandidates.length,
        }
      );
      return [];
    }

    const entries = targetCandidates
      .map(({ target, key, fallbackTargets = [] }) => {
        const candidates = [{ target, key }, ...fallbackTargets];

        for (const candidate of candidates) {
          try {
            const resolvedLayer = motionAPI.inspect.resolveTarget(
              jsonFile as any,
              candidate.target
            )[0]?.layer;
            const capabilitiesStartedAt = studioPerfNow();
            const capabilities = motionAPI.inspect.getEditableLayerCapabilities(
              jsonFile as any,
              candidate.target,
              { frame: inspectionFrame }
            )[0];
            recordStudioPerf(
              "LayerVisualPropertiesCapabilities getEditableLayerCapabilities",
              studioPerfNow() - capabilitiesStartedAt,
              {
                targetKey: candidate.key,
              }
            );
            if (!capabilities) {
              continue;
            }

            return {
              target: capabilities.target,
              key: candidate.key,
              capabilities,
              resolvedLayer,
            };
          } catch {
            continue;
          }
        }

        return undefined;
      })
      .filter((entry): entry is CapabilityEntry => Boolean(entry));
    recordStudioPerf(
      "LayerVisualPropertiesCapabilities base",
      studioPerfNow() - startedAt,
      {
        entries: entries.length,
        frame: Math.round(inspectionFrame),
        resolvedTargetKey: entries[0]?.key,
        targetKey: targetCandidates[0]?.key,
        targets: targetCandidates.length,
      }
    );
    return entries;
  }, [jsonFile, targetCandidates]);

  const controlReportTargetSignature = useMemo(
    () => baseCapabilityEntries.map((entry) => entry.key).join("|"),
    [baseCapabilityEntries]
  );

  const target =
    baseCapabilityEntries[0]?.target ?? targetCandidates[0]?.target ?? null;

  const fontsInspection = useMemo(() => {
    if (!jsonFile) {
      return undefined;
    }

    try {
      return motionAPI.inspect.getFonts(jsonFile as any);
    } catch {
      return undefined;
    }
  }, [jsonFile]);

  const layerFont = useMemo(() => {
    if (!jsonFile || !target) {
      return undefined;
    }

    try {
      return motionAPI.inspect.getLayerFont(jsonFile as any, target);
    } catch {
      return undefined;
    }
  }, [jsonFile, target]);

  const availableFonts = useMemo(() => {
    try {
      return motionAPI.inspect.getAvailableFonts();
    } catch {
      return [];
    }
  }, []);

  const fontOptions = useMemo(
    () =>
      buildFontSelectOptions(
        fontsInspection,
        layerFont,
        availableFonts,
        accountFonts
      ),
    [accountFonts, availableFonts, fontsInspection, layerFont]
  );

  const [controlReportState, setControlReportState] = useState<
    ControlReportState
  >();

  useEffect(() => {
    let isActive = true;
    const startedAt = studioPerfNow();
    const inspectionFrame = latestFrameRef.current;

    if (!jsonFile || !baseCapabilityEntries.length) {
      setControlReportState(undefined);
      recordStudioPerf(
        "LayerVisualPropertiesCapabilities controlReport",
        studioPerfNow() - startedAt,
        {
          frame: Math.round(inspectionFrame),
          reports: 0,
          resolvedTargetKey: baseCapabilityEntries[0]?.key,
          targetKey: targetCandidates[0]?.key,
          targets: baseCapabilityEntries.length,
        }
      );
      return;
    }

    const options = {
      frame: inspectionFrame,
      mods: mods as any,
      data: (data as Record<string, unknown>) || {},
      liveControlData: (liveControlData as Record<string, unknown>) || {},
    };

    Promise.all(
      baseCapabilityEntries.map(({ target }) =>
        motionAPI.inspect
          .getLayerControlReport(jsonFile as any, target, options)
          .then(([report]) => report)
          .catch(() => undefined)
      )
    )
      .then((reports) => {
        const nextReports = reports.filter(
          (report): report is MotionLayerControlReport => Boolean(report)
        );
        recordStudioPerf(
          "LayerVisualPropertiesCapabilities controlReport",
          studioPerfNow() - startedAt,
          {
            frame: Math.round(inspectionFrame),
            reports: nextReports.length,
            resolvedTargetKey: baseCapabilityEntries[0]?.key,
            targetKey: targetCandidates[0]?.key,
            targets: baseCapabilityEntries.length,
          }
        );
        if (isActive) {
          setControlReportState({
            reports: nextReports,
            jsonFile,
            targetSignature: controlReportTargetSignature,
            mods,
            data,
            liveControlData,
          });
        }
      })
      .catch(() => {
        recordStudioPerf(
          "LayerVisualPropertiesCapabilities controlReport error",
          studioPerfNow() - startedAt,
          {
            frame: Math.round(inspectionFrame),
            resolvedTargetKey: baseCapabilityEntries[0]?.key,
            targetKey: targetCandidates[0]?.key,
            targets: baseCapabilityEntries.length,
          }
        );
        if (isActive) {
          setControlReportState(undefined);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    baseCapabilityEntries,
    controlReportTargetSignature,
    data,
    jsonFile,
    liveControlData,
    mods,
    targetCandidates,
  ]);

  const controlReports = useMemo(
    () =>
      controlReportState &&
      controlReportState.jsonFile === jsonFile &&
      controlReportState.targetSignature === controlReportTargetSignature &&
      controlReportState.mods === mods &&
      controlReportState.data === data &&
      controlReportState.liveControlData === liveControlData
        ? controlReportState.reports
        : [],
    [
      controlReportState,
      data,
      jsonFile,
      liveControlData,
      mods,
      controlReportTargetSignature,
    ]
  );

  const controlReportByTargetKey = useMemo(
    () =>
      new Map(
        controlReports.map((report) => [getTargetKey(report.target), report])
      ),
    [controlReports]
  );

  const capabilityEntries = useMemo(
    () =>
      baseCapabilityEntries.map((entry) => ({
        ...entry,
        controlReport: controlReportByTargetKey.get(getTargetKey(entry.target)),
        capabilities:
          controlReportByTargetKey.get(getTargetKey(entry.target))
            ?.capabilities ?? entry.capabilities,
      })),
    [baseCapabilityEntries, controlReportByTargetKey]
  );

  const fieldLookups = useMemo(() => {
    const startedAt = studioPerfNow();
    const lookups = capabilityEntries.map((entry) => {
      const preparedFields = prepareFieldsForLayer(
        (entry.controlReport?.fields ??
          entry.capabilities.fields) as VisualEditableField[],
        entry.resolvedLayer,
        currentFrame
      );
      const connectionRefs =
        entry.controlReport?.connections.map(toConnectionRef) ?? [];

      return buildFieldLookup(
        mergeConnectionControlsIntoFields(preparedFields, connectionRefs)
      );
    });
    recordStudioPerf(
      "LayerVisualPropertiesCapabilities fieldLookups",
      studioPerfNow() - startedAt,
      {
        entries: capabilityEntries.length,
        fields: lookups.reduce((count, lookup) => count + lookup.size, 0),
        frame: Math.round(currentFrame),
      }
    );
    return lookups;
  }, [capabilityEntries, currentFrame]);

  const editableFields = useMemo(() => buildCommonFieldLookup(fieldLookups), [
    fieldLookups,
  ]);

  const effectiveCapabilities = useMemo(
    () =>
      isMultiSelection
        ? buildMultiCapabilities(capabilityEntries, editableFields)
        : capabilityEntries[0]?.capabilities,
    [capabilityEntries, editableFields, isMultiSelection]
  );

  const controlReport = capabilityEntries[0]?.controlReport;
  const resolvedLayer = capabilityEntries[0]?.resolvedLayer;

  const activeTextDocumentIndex = useMemo(() => {
    const textDocuments = (resolvedLayer as
      | {
          t?: {
            d?: { k?: Array<{ t?: number; s?: Record<string, unknown> }> };
          };
        }
      | undefined)?.t?.d?.k;

    return Array.isArray(textDocuments) && textDocuments.length
      ? getActiveTextDocumentIndexForFrame(textDocuments, currentFrame)
      : undefined;
  }, [currentFrame, resolvedLayer]);

  const activeTextDocumentStyle = useMemo(() => {
    const textDocuments = (resolvedLayer as
      | {
          t?: {
            d?: { k?: Array<{ t?: number; s?: Record<string, unknown> }> };
          };
        }
      | undefined)?.t?.d?.k;

    return typeof activeTextDocumentIndex === "number"
      ? textDocuments?.[activeTextDocumentIndex]?.s
      : undefined;
  }, [activeTextDocumentIndex, resolvedLayer]);

  const textContentField = useMemo(() => {
    if (isMultiSelection) {
      return editableFields.get("text");
    }

    return resolveTextDocumentFieldsForFrame(
      normalizeEditableFieldAnimationMetadata(
        [
          controlReport?.fields.find((field) => field.property === "text"),
        ].filter(Boolean) as VisualEditableField[],
        currentFrame
      ),
      resolvedLayer,
      currentFrame
    )[0];
  }, [
    controlReport,
    currentFrame,
    editableFields,
    isMultiSelection,
    resolvedLayer,
  ]);

  const activeAnchorPosition = useMemo<AnchorPosition | undefined>(() => {
    const startedAt = studioPerfNow();
    const anchorField = editableFields.get("anchor");

    if (!jsonFile || !target || !anchorField) {
      recordStudioPerf(
        "LayerVisualPropertiesCapabilities activeAnchorPosition",
        studioPerfNow() - startedAt,
        {
          frame: Math.round(currentFrame),
          hasAnchorField: Boolean(anchorField),
          matched: false,
        }
      );
      return undefined;
    }

    try {
      const activePosition = ANCHOR_POSITIONS.find((position) => {
        const computed = motionAPI.inspect.getAnchorPosition(
          jsonFile as any,
          target,
          {
            position: position.value,
            frame: currentFrame,
          }
        );

        return areVectorsClose(computed.anchor, anchorField.value);
      })?.value;
      recordStudioPerf(
        "LayerVisualPropertiesCapabilities activeAnchorPosition",
        studioPerfNow() - startedAt,
        {
          frame: Math.round(currentFrame),
          hasAnchorField: true,
          matched: Boolean(activePosition),
        }
      );
      return activePosition;
    } catch {
      recordStudioPerf(
        "LayerVisualPropertiesCapabilities activeAnchorPosition error",
        studioPerfNow() - startedAt,
        {
          frame: Math.round(currentFrame),
          hasAnchorField: true,
        }
      );
      return undefined;
    }
  }, [currentFrame, editableFields, jsonFile, target]);

  const canAddFill = useMemo(
    () =>
      effectiveCapabilities?.addableActions.some(
        (action) => action.action === "addFill" && action.enabled
      ) ?? false,
    [effectiveCapabilities]
  );

  const canAddOutline = useMemo(
    () =>
      effectiveCapabilities?.addableActions.some(
        (action) => action.action === "addOutline" && action.enabled
      ) ?? false,
    [effectiveCapabilities]
  );

  const hasCuratedFields = useMemo(() => {
    const hasFallbackColorControls =
      !isMultiSelection &&
      (effectiveCapabilities?.layerKind === "text" ||
        effectiveCapabilities?.layerKind === "solid" ||
        (effectiveCapabilities?.layerKind === "shape" &&
          (canAddFill || canAddOutline)));

    return (
      hasFallbackColorControls ||
      CURATED_PROPERTIES.some((property) => editableFields.has(property))
    );
  }, [
    canAddFill,
    canAddOutline,
    effectiveCapabilities?.layerKind,
    editableFields,
    isMultiSelection,
  ]);

  const mediaControls = useMemo<MotionConnectionRef[]>(
    () =>
      (controlReport?.connections ?? [])
        .filter(
          (connection) =>
            !connection.disabled && connection.writeBehavior === "replace-asset"
        )
        .map((connection) => ({
          ...toConnectionRef(connection),
        })),
    [controlReport]
  );
  const targets = useMemo(
    () =>
      capabilityEntries.length
        ? capabilityEntries.map((entry) => entry.target)
        : targetCandidates.map((entry) => entry.target),
    [capabilityEntries, targetCandidates]
  );

  return {
    target,
    targets,
    capabilities: effectiveCapabilities,
    editableFields,
    textContentField,
    fontOptions,
    layerFont,
    mediaControls,
    activeAnchorPosition,
    activeTextDocumentIndex,
    activeTextDocumentStyle,
    canAddFill,
    canAddOutline,
    hasCuratedFields,
  };
}
